import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import Header from "@/components/Header";
import ImpactCard from "@/components/ImpactCard";
import ShareModal from "@/components/ShareModal";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Target, CheckCircle2, Gauge, Clock, DollarSign, Flame, Share2, Trophy, Lock, Flag, Info,
} from "lucide-react";

const EMPTY_IMPACT = {
  moves_created: 0, blockers_cleared: 0, actions_completed: 0, actions_committed: 0, action_rate: 0,
  time_saved_label: "0m", money_impact: 0, streak: 0, minutescore: null, tier: "Getting Started",
  score_status: "not_enough_data", score_cap: 0, moves_to_establish: 5, milestones: [], display_name: "",
};

function Kpi({ icon: Icon, label, value, sub, color, i, testId, tip }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
      className="rounded-2xl border border-white/10 bg-[var(--surface)] p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 font-mono2 text-[10px] uppercase tracking-wider text-neutral-500">
          {label}
          {tip && (
            <span title={tip} data-testid={testId ? `${testId}-tip` : undefined}
              className="inline-flex cursor-help text-neutral-600 hover:text-neutral-300">
              <Info className="h-3 w-3" />
            </span>
          )}
        </span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <p className="font-head mt-3 text-3xl font-black text-white" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
      {sub && <p className="mt-1 text-xs text-neutral-500">{sub}</p>}
    </motion.div>
  );
}

const scoreColor = (s) => (s >= 90 ? "#10B981" : s >= 80 ? "#FF6B35" : s >= 55 ? "#F59E0B" : "#F43F5E");

function MinuteScoreGauge({ data }) {
  const { minutescore: score, tier, score_status, actions_completed, moves_to_establish, score_cap } = data;
  const hasScore = score !== null && score !== undefined;
  const r = 52; const c = 2 * Math.PI * r; const off = hasScore ? c - (score / 100) * c : c;
  const color = hasScore ? scoreColor(score) : "#656C7B";
  const provisional = score_status === "provisional";
  return (
    <div className="flex flex-col items-center rounded-2xl border border-white/10 bg-[var(--surface)] p-7" data-testid="minutescore-panel">
      <span className="eyebrow" data-testid="minutescore-label">{provisional ? "Provisional MinuteScore" : "MinuteScore"}</span>
      <div className="relative mt-4 h-36 w-36">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
          <motion.circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
            strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: off }} transition={{ duration: 1.1, ease: "easeOut" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-head text-4xl font-black text-white" data-testid="minutescore-value">{hasScore ? score : "—"}</span>
          <span className="font-mono2 text-[9px] uppercase tracking-wider text-neutral-500">{hasScore ? "/ 100" : "no data yet"}</span>
        </div>
      </div>
      <span className="mt-4 rounded-full px-3 py-1 text-sm font-semibold" style={{ color, background: color + "14" }} data-testid="minutescore-tier">
        {hasScore ? tier : "Not enough data"}
      </span>
      {data.score_insight && (
        <p className="mt-4 text-center text-sm leading-relaxed text-neutral-300" data-testid="minutescore-insight">{data.score_insight}</p>
      )}
      {!hasScore && (
        <p className="mt-3 text-center text-xs leading-relaxed text-neutral-500" data-testid="minutescore-evidence">
          Complete your first Move to establish a MinuteScore. It measures follow-through — not how many questions you ask.
        </p>
      )}
      {provisional && (
        <p className="mt-3 text-center text-xs leading-relaxed text-neutral-500" data-testid="minutescore-evidence">
          <span className="text-neutral-300">{actions_completed} of 5 Moves completed</span> · scores capped at {score_cap} for now.
          Complete {moves_to_establish} more {moves_to_establish === 1 ? "Move" : "Moves"} to establish your full MinuteScore.
        </p>
      )}
      {hasScore && !provisional && (
        <p className="mt-3 text-center text-xs leading-relaxed text-neutral-500" data-testid="minutescore-evidence">
          40% follow-through · 20% commitment integrity · 15% outcomes reported · 15% decision efficiency · 10% consistency.
        </p>
      )}
    </div>
  );
}

function Milestones({ milestones }) {
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-[var(--surface)] p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="eyebrow flex items-center gap-2"><Trophy className="h-3.5 w-3.5 text-[var(--amber)]" /> Milestones</span>
        <span className="text-xs text-neutral-600">Historical achievements — your live MinuteScore is shown separately above.</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {milestones.map((m) => (
          <span key={m.key} data-testid={`milestone-${m.key}`} data-unlocked={m.unlocked}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm ${m.unlocked ? "border-[var(--amber)]/40 bg-[var(--amber)]/10 text-white" : "border-white/10 text-neutral-600"}`}>
            {m.unlocked ? <Flag className="h-3.5 w-3.5 text-[var(--amber)]" /> : <Lock className="h-3.5 w-3.5" />}
            {m.unlocked ? m.unlocked_label : m.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Impact() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = () => {
    api.get("/impact")
      .then(({ data }) => setData({ ...EMPTY_IMPACT, ...(data || {}) }))
      .catch(() => { setFailed(true); setData({ ...EMPTY_IMPACT }); });
    api.get("/impact/trend")
      .then(({ data }) => setTrend(Array.isArray(data?.trend) ? data.trend : []))
      .catch(() => setTrend([]));
  };
  useEffect(load, []);

  if (!data) return <div className="min-h-screen"><Header /><p className="p-10 text-neutral-500" data-testid="impact-loading">Loading impact…</p></div>;

  const milestones = Array.isArray(data.milestones) ? data.milestones : [];
  const isEmpty = data.moves_created === 0 && data.actions_committed === 0;

  return (
    <div className="min-h-screen" data-testid="impact-page">
      <Header />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="eyebrow">Your executive action ledger</span>
            <h1 className="font-head mt-2 text-4xl font-black tracking-tight text-white">Impact Dashboard</h1>
            <p className="mt-2 text-neutral-400">The scoreboard is actions completed — not time spent here.</p>
          </div>
          <Button data-testid="open-share-btn" onClick={() => setShareOpen(true)} className="btn-amber rounded-full px-6">
            <Share2 className="mr-1 h-4 w-4" /> Share my impact
          </Button>
        </div>

        {failed && (
          <p className="mt-6 rounded-xl border border-white/10 bg-[var(--surface)] px-4 py-3 text-sm text-neutral-400" data-testid="impact-error">
            Couldn't load your latest numbers. Showing a baseline — <button onClick={load} className="text-[var(--amber)] hover:underline">retry</button>.
          </p>
        )}

        {isEmpty && !failed && (
          <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-xl border border-[var(--amber)]/30 bg-[var(--amber)]/[0.05] px-5 py-4 sm:flex-row sm:items-center" data-testid="impact-empty-state">
            <div>
              <p className="text-sm font-semibold text-white">Your ledger is empty — for now.</p>
              <p className="mt-0.5 text-xs text-neutral-400">Every number below moves the moment you clear your first blocker and commit.</p>
            </div>
            <Link to="/app" data-testid="impact-empty-cta" className="text-sm font-semibold text-[var(--amber)] hover:underline">Clear your first blocker →</Link>
          </div>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Kpi i={0} testId="kpi-moves-created" icon={Target} label="Moves Created" value={data.moves_created} sub={`${data.blockers_cleared} resolved · ${data.actions_committed} committed`} color="#FF6B35" />
          <Kpi i={1} testId="kpi-actions-completed" icon={CheckCircle2} label="Actions Completed" value={data.actions_completed} sub={data.partial ? `+ ${data.partial} partly completed` : "moves you reported completing"} color="#10B981" />
          <Kpi i={2} testId="kpi-follow-through" icon={Gauge} label="Follow-Through" value={`${data.action_rate}%`} sub="completed ÷ committed moves" color="#FF6B35" tip="Completed eligible committed actions ÷ all eligible committed actions. An open decision-avoidance loop isn't a committed action, so it never counts as completed here." />
          <Kpi i={3} testId="kpi-time-saved" icon={Clock} label="Decision Time Saved" value={data.time_saved_label} sub="user-reported, aggregated" color="#A0A6B2" />
          <Kpi i={4} testId="kpi-money" icon={DollarSign} label="User-Reported Impact" value={`£${data.money_impact}`} sub="self-reported, not verified" color="#10B981" />
          <Kpi i={5} testId="kpi-streak" icon={Flame} label="Action Streak" value={`${data.streak} ${data.streak === 1 ? "day" : "days"}`} sub={data.streak > 0 ? "consecutive days with a completed action" : "start one today"} color="#F59E0B" />
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[var(--surface)] p-6">
            <span className="eyebrow">Actions completed · last 14 days</span>
            <div className="mt-4 h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF6B35" stopOpacity={0.5} /><stop offset="100%" stopColor="#FF6B35" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="date" tick={{ fill: "#656C7B", fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
                  <Tooltip contentStyle={{ background: "#1C2027", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} cursor={{ stroke: "rgba(255,255,255,0.1)" }} />
                  <Area type="monotone" dataKey="completed" stroke="#FF6B35" strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <MinuteScoreGauge data={data} />
        </div>

        <Milestones milestones={milestones} />

        <div className="mt-8">
          <span className="eyebrow">Your shareable card · preview</span>
          <div className="mt-3 max-w-2xl"><ImpactCard data={data} /></div>
        </div>
      </main>

      <ShareModal open={shareOpen} onOpenChange={setShareOpen} impact={data} milestones={milestones.filter((m) => m.unlocked)} />
    </div>
  );
}
